import { shipmentOrder } from '@/domain/shipment-order';
import { useMemo } from "react";
import { useCurrentWorkspace } from "./useWorkspaceData";
import { useOperationOverrides } from "./useOperationOverrides";
import { useAssetStore } from "@/stores/assetStore";
import { useShipmentStore } from "@/stores/shipmentStore";
import {
  useShareLinkStore,
  type ConsoleShareLink,
} from "@/stores/shareLinkStore";
import type { AssetType, ShareFilters, ShipmentFilters } from "@/domain";
/** Persisted application metadata overlays the workspace's observation queries. */
export function useLocationData() {
  const data = useOperationOverrides(useCurrentWorkspace());
  const assetState = useAssetStore();
  const shipmentState = useShipmentStore();
  const shareState = useShareLinkStore();
  return useMemo(() => {
    const registrations = assetState.registrations.filter(
      (r) => r.asset.workspaceId === data.workspace.id,
    );
    const assets = [
      ...data.listAssets(),
      ...registrations.map((r) => r.asset),
    ].map((a) => ({ ...a, ...assetState.overrides[a.id] }));
    const devices = [
      ...data.listDevices(),
      ...registrations.map((r) => r.device),
    ];
    const shipments = [
      ...shipmentState.shipments.filter(
        (s) => s.workspaceId === data.workspace.id,
      ),
      ...data.listShipments(),
    ];
    const shares: ConsoleShareLink[] = [
      ...shareState.created.filter((s) => s.workspaceId === data.workspace.id),
      ...data
        .listShareLinks()
        .map((s) => ({
          ...s,
          name: s.scope.kind === 'asset' ? `${data.getAsset(s.scope.assetId)?.name ?? 'Asset'} location` : s.scope.kind === 'shipment' ? `${data.getShipment(s.scope.shipmentId)?.reference ?? 'Shipment'} delivery` : 'Group locations',
          recipientLabel:
            data.getRecipient(s.recipientId)?.name ?? "External viewer",
          access: "latest" as const,
        })),
    ].map((s) => ({ ...s, ...shareState.overrides[s.id] }));
    return {
      ...data,
      listAssets: (type?: AssetType) =>
        assets.filter((a) => !type || a.type === type),
      getAsset: (id: string) => assets.find((a) => a.id === id),
      getAssetForDevice: (id: string) => assets.find((a) => a.deviceId === id),
      getDevice: (id: string) => devices.find((d) => d.id === id),
      listShipments: (filters: ShipmentFilters = {}) =>
        shipments
          .filter(
            (s) =>
              (!filters.status || s.status === filters.status) &&
              (!filters.assetId || s.assetIds.includes(filters.assetId)) &&
              (!filters.search ||
                s.reference
                  .toLowerCase()
                  .includes(filters.search.toLowerCase())),
          )
          .sort(shipmentOrder),
      getShipment: (id: string) => shipments.find((s) => s.id === id),
      getShipmentCheckpoints: (id: string) =>
        [
          ...data.getShipmentCheckpoints(id),
          ...shipmentState.checkpoints.filter(
            (c) => c.shipmentId === id && c.workspaceId === data.workspace.id,
          ),
        ].sort((a, b) => a.sequence - b.sequence),
      listShareLinks: (filters: ShareFilters = {}) =>
        shares.filter(
          (s) =>
            (!filters.status ||
              filters.status ===
                (s.revokedAt
                  ? "revoked"
                  : s.expiresAt <= Math.max(data.now, Date.now())
                    ? "expired"
                    : "active")) &&
            (!filters.assetId ||
              (s.scope.kind === "asset" &&
                s.scope.assetId === filters.assetId)) &&
            (!filters.shipmentId ||
              (s.scope.kind === "shipment" &&
                s.scope.shipmentId === filters.shipmentId)),
        ),
      getShareLink: (id: string) => shares.find((s) => s.id === id),
    };
  }, [data, assetState, shipmentState, shareState]);
}
export type LocationData = ReturnType<typeof useLocationData>;
